/**
 * ============================================================================
 * DiscountExamples - SAVINGS CALCULATOR DISPLAY
 * ============================================================================
 *
 * Shows example savings based on discount percentages
 * Extracted from inline calculation logic
 *
 * ============================================================================
 */

import { CURRENCY_SYMBOL } from '../../utils/currency';
import React from 'react';
import { calculateDiscountSavings } from '../../utils/courtFormatters';

interface DiscountExamplesProps {
  discount1Month: number | '';
  discount3Months: number | '';
  discount6Months: number | '';
  discount12Months: number | '';
  baseRate?: number;
}

/**
 * Discount savings examples
 */
export function DiscountExamples({
  discount1Month,
  discount3Months,
  discount6Months,
  discount12Months,
  baseRate = 10000,
}: DiscountExamplesProps) {
  // Convert empty strings to 0 for calculations
  const d1 = discount1Month === '' ? 0 : discount1Month;
  const d3 = discount3Months === '' ? 0 : discount3Months;
  const d6 = discount6Months === '' ? 0 : discount6Months;
  const d12 = discount12Months === '' ? 0 : discount12Months;

  const hasAnyDiscount = d1 > 0 || d3 > 0 || d6 > 0 || d12 > 0;

  if (!hasAnyDiscount) return null;

  const examples = [
    {
      label: '1 Month (4 weeks)',
      discount: d1,
      weeks: 4,
      totalCost: baseRate * 4,
    },
    {
      label: '3 Months (12 weeks)',
      discount: d3,
      weeks: 12,
      totalCost: baseRate * 12,
    },
    {
      label: '6 Months (24 weeks)',
      discount: d6,
      weeks: 24,
      totalCost: baseRate * 24,
    },
    {
      label: '12 Months (48 weeks)',
      discount: d12,
      weeks: 48,
      totalCost: baseRate * 48,
    },
  ].filter((ex) => ex.discount > 0);

  return (
    <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-md">
      <p className="text-xs font-semibold text-green-900 mb-2">Example Savings ({CURRENCY_SYMBOL}{baseRate.toLocaleString()}/session):</p>
      <div className="text-xs text-green-800 space-y-1">
        {examples.map((ex, idx) => {
          const savings = calculateDiscountSavings(baseRate, ex.discount, ex.weeks);
          return (
            <p key={idx}>
              • {ex.label}: {ex.discount}% off = Save {CURRENCY_SYMBOL}{Math.round(savings).toLocaleString()} on {CURRENCY_SYMBOL}
              {ex.totalCost.toLocaleString()}
            </p>
          );
        })}
      </div>
    </div>
  );
}
